import { useGlobalStore, useGlobalStoreSelector } from '@/stores/global.store';
import { boot, Intercom, show, shutdown } from '@intercom/messenger-js-sdk';
import { useEffect, useRef } from 'react';
import { useMatches } from '@tanstack/react-router';

// Authenticated surfaces that sit outside the /_authenticated tree.
const EXTRA_AUTHENTICATED_ROUTE_IDS = ['/upgrades'];

const LAUNCHER_HIDDEN_ROUTE_IDS = ['/_authenticated/practice-call', '/_authenticated/super-admin'];

// The messenger is never booted in E2E, so calling show() there throws on the dummy app id.
export const openIntercom = () => {
	if (import.meta.env.VITE_APP_ENV === 'e2e') return;
	show();
};

export const useIntercom = () => {
	const matches = useMatches();
	const { store } = useGlobalStore();
	const userId = useGlobalStoreSelector((s) => s?.user?.id);
	const companyId = useGlobalStoreSelector((s) => s?.currentCompany?.id);
	const wasShutDown = useRef(false);

	const isAuthenticatedRoute = matches.some(
		(match) => match.routeId.startsWith('/_authenticated') || EXTRA_AUTHENTICATED_ROUTE_IDS.includes(match.routeId),
	);

	const isLauncherHidden = matches.some((match) => LAUNCHER_HIDDEN_ROUTE_IDS.includes(match.routeId));

	useEffect(() => {
		if (import.meta.env.VITE_APP_ENV === 'e2e') return;

		// Google Tag Manager boots the messenger on every page load, so shut down unconditionally.
		if (!isAuthenticatedRoute) {
			shutdown();
			wasShutDown.current = true;
			return;
		}

		const state = store.getState();
		if (!state.user || !state.currentCompany) return;

		const settings = {
			app_id: import.meta.env.VITE_INTERCOM_APP_ID,
			alignment: 'left',
			hide_default_launcher: isLauncherHidden,
			horizontal_padding: 20,
			vertical_padding: 20,
			email: state.user.email,
			'Company Name': state.currentCompany.name,
			'Company id': state.currentCompany.id,
			Subdomain: state.currentCompany.subDomain,
			user_id: state.user.id,
			name: `${state.user.firstName} ${state.user.lastName}`,
			'Stripe plan': state.currentCompany?.companyBillingEntity?.plan,
			Env: import.meta.env.VITE_APP_ENV,
		};

		// Intercom() only reaches `update` after a shutdown, which never restarts the messenger.
		if (wasShutDown.current) {
			boot(settings);
			wasShutDown.current = false;
			return;
		}

		Intercom(settings);
	}, [isAuthenticatedRoute, isLauncherHidden, userId, companyId, store]);

	return null;
};
